import React from 'react';
import { UserConfig } from '../../lib/types';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/Table';
import { Button } from '../ui/Button';
import { PlayCircle, Cog, Trash2 } from 'lucide-react';

interface UserConfigTableProps {
  configs: UserConfig[];
  onEdit: (config: UserConfig) => void;
  onDelete: (config: UserConfig) => void;
  onTestTrade: (config: UserConfig) => void;
}

type StatusTone = 'success' | 'muted' | 'pending';

const statusToneClasses: Record<StatusTone, string> = {
  success: 'bg-green-100 text-green-800',
  muted: 'bg-gray-100 text-gray-600',
  pending: 'bg-amber-100 text-amber-800',
};

const StatusBadge: React.FC<{ label: string; tone?: StatusTone }> = ({ label, tone = 'success' }) => (
  <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${statusToneClasses[tone]}`}>
    {label}
  </span>
);

export const UserConfigTable: React.FC<UserConfigTableProps> = ({
  configs,
  onEdit,
  onDelete,
  onTestTrade,
}) => {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>User</TableHead>
          <TableHead>Trading</TableHead>
          <TableHead>Testnet Keys</TableHead>
          <TableHead>Production Keys</TableHead>
          <TableHead>Proxy</TableHead>
          <TableHead>Notifications</TableHead>
          <TableHead>Reports</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {configs.map((config) => {
          const testnetReady = Boolean(
            config.bybitTestnetApiKey &&
              config.bybitTestnetApiSecret &&
              config.testnetApiKey
          );
          const prodReady = Boolean(
            config.bybitProductionApiKey &&
              config.bybitProductionApiSecret &&
              config.productionApiKey
          );
          const proxyConfigured = Boolean(config.proxyHost && config.proxyPort);
          const notificationStatus = (config.notificationStatus || '').toLowerCase();
          const isNotificationPending = config.notificationsEnabled && notificationStatus.includes('pending');
          const sesStatus = (config.sesStatus || '').toLowerCase();
          const isReportsPending = config.reportsEnabled && sesStatus.includes('pending');
          return (
            <TableRow key={config.id}>
              <TableCell>
                <div className="flex flex-col">
                  <span className="font-medium text-gray-900">{config.name}</span>
                  <span className="text-xs text-gray-500">ID: {config.id}</span>
                  {config.email && (
                    <span className="text-xs text-gray-500">{config.email}</span>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <StatusBadge
                  label={config.tradingEnabled ? 'Enabled' : 'Disabled'}
                  tone={config.tradingEnabled ? 'success' : 'muted'}
                />
              </TableCell>
              <TableCell>
                <StatusBadge label={testnetReady ? 'Ready' : 'Missing'} tone={testnetReady ? 'success' : 'muted'} />
              </TableCell>
              <TableCell>
                <StatusBadge label={prodReady ? 'Ready' : 'Missing'} tone={prodReady ? 'success' : 'muted'} />
              </TableCell>
              <TableCell>
                <StatusBadge label={proxyConfigured ? 'Configured' : 'None'} tone={proxyConfigured ? 'success' : 'muted'} />
              </TableCell>
              <TableCell>
                {config.notificationsEnabled ? (
                  <StatusBadge
                    label={isNotificationPending ? 'Pending' : 'Enabled'}
                    tone={isNotificationPending ? 'pending' : 'success'}
                  />
                ) : (
                  <StatusBadge label="Disabled" tone="muted" />
                )}
              </TableCell>
              <TableCell>
                {config.reportsEnabled ? (
                  <StatusBadge
                    label={isReportsPending ? 'Pending' : 'Enabled'}
                    tone={isReportsPending ? 'pending' : 'success'}
                  />
                ) : (
                  <StatusBadge label="Disabled" tone="muted" />
                )}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => onTestTrade(config)}
                    title="Run test trade"
                  >
                    <PlayCircle className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => onEdit(config)}
                    title="Edit configuration"
                  >
                    <Cog className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => onDelete(config)}
                    title="Delete configuration"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
